<script setup lang="ts">
/**
 * Unsubscribed (Figma `3603:68616`).
 *
 * Opened from "Unsubscribe" in a marketing email: one click, no sign-in, only
 * that email's group is switched off. Undo puts it back on; Manage preferences
 * opens Notification settings (Log in first), where "Unsubscribe from all" is.
 */
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { FairButton } from '@/ui/primitives/FairButton'
import { useFairToast } from '@/ui/patterns/FairToast'
import { useAuthModal } from '@/features/auth/store'
import AuthScreenShell from '@/features/auth/ui/parts/AuthScreenShell.vue'
import { PROFILE_NOTIFICATIONS_ROUTE } from '@/router/routes'
import successUrl from '@/assets/img/auth/illustration-password-updated.png'
import { resubscribeGroup } from '../api/emailLinks'
import { isEmailLinksActionAvailable } from '../flags'
import { useEmailLinkStore } from '../store'
import { useSignedInAction } from './useSignedInAction'

const { t } = useI18n()
const router = useRouter()
const toast = useFairToast()
const authModal = useAuthModal()
const links = useEmailLinkStore()
const runSignedIn = useSignedInAction()

const groupLabel = computed(() => t(`email_links.groups.${links.unsubscribedGroup}`))
const undoAvailable = isEmailLinksActionAvailable('group_unsubscribe')
const undoing = ref(false)
const undone = ref(false)

function managePreferences() {
  runSignedIn(() => {
    authModal.hideAuthModal()
    router.push(PROFILE_NOTIFICATIONS_ROUTE)
  })
}

async function undo() {
  undoing.value = true
  try {
    await resubscribeGroup(links.unsubscribeToken)
    undone.value = true
    toast.show({ message: t('email_links.toast.group_back_on', { group: groupLabel.value }) })
  } catch {
    toast.show({ message: t('email_links.errors.generic'), tone: 'warning' })
  } finally {
    undoing.value = false
  }
}
</script>

<template>
  <AuthScreenShell
    centered
    :title="t('email_links.unsubscribed.title')"
    :description="t('email_links.unsubscribed.description', { group: groupLabel })"
  >
    <template #art>
      <img
        class="fair-auth-screen__art"
        :src="successUrl"
        alt=""
      >
    </template>

    <div class="fair-auth-actions">
      <FairButton
        class="fair-auth-cta"
        size="auto"
        :label="t('email_links.unsubscribed.manage')"
        @click="managePreferences"
      />
      <FairButton
        v-if="undoAvailable && !undone"
        class="fair-auth-cta"
        size="auto"
        variant="tonal"
        :label="t('email_links.undo')"
        :loading="undoing"
        @click="undo"
      />
    </div>
  </AuthScreenShell>
</template>
